import { Head } from "@inertiajs/react"
import { Box, Flex, Heading, Table, Text } from "@radix-ui/themes"
import { Check, X } from "lucide-react"
import type { ReactNode } from "react"
import WeightHistoryChart from "@/components/weight-history-chart"
import type { WeightHistory } from "@/components/weight-history-chart"

interface RecentDay {
  date_string: string
  is_today: boolean
  total_calories: number | null
  is_confirmed: boolean
  weight_kg: number | null
  weight_trend_kg: number | null
}

interface WeekStat {
  id: string
  week_start_date: string
  average_calories: number | null
  weight_change_trend_kg: number | null
  tracked_days_count: number
  completed_days_count: number
}

interface DashboardIndexProps {
  weight_history: WeightHistory
  recent_days: RecentDay[]
  week_stats: WeekStat[]
}

const weightChangeWindowDays = 7

function formatWeekRange(weekStartDate: string) {
  const startDate = parseDate(weekStartDate)
  const endDate = new Date(startDate)
  endDate.setUTCDate(startDate.getUTCDate() + 6)

  return `${formatShortDate(startDate)}-${formatShortDate(endDate)}`
}

function formatShortDate(date: Date) {
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  })
}

function formatDayDate(dateString: string) {
  return parseDate(dateString).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  })
}

function formatCalorieTotal(value: number | null) {
  if (value === null) return "—"

  return `${Math.round(value).toLocaleString()} kcal`
}

function formatCalorieAverage(value: number | null) {
  if (value === null) return "—"

  return `${Math.round(value).toLocaleString()} kcal/day`
}

function formatWeightChange(value: number | null) {
  if (value === null) return "—"

  const weeklyChange = value * weightChangeWindowDays
  return `${weeklyChange >= 0 ? "+" : ""}${weeklyChange.toFixed(1)} kg/week`
}

function formatWeight(value: number | null) {
  if (value === null) return "—"

  return `${value.toFixed(1)} kg`
}

function formatTrackedDays(weekStat: WeekStat) {
  const suffix = weekStat.completed_days_count < 7 ? " so far" : ""
  return `${weekStat.tracked_days_count}/${weekStat.completed_days_count}${suffix}`
}

function parseDate(dateString: string) {
  return new Date(`${dateString}T00:00:00Z`)
}

function TrackingIcon({ isConfirmed }: { isConfirmed: boolean }) {
  if (isConfirmed) {
    return (
      <Check
        aria-label="Included in average"
        color="var(--green-9)"
        size={14}
        strokeWidth={2.5}
      />
    )
  }

  return (
    <X
      aria-label="Not included in average"
      color="var(--red-9)"
      size={14}
      strokeWidth={2.5}
    />
  )
}

function DashboardSection({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle: string
  children: ReactNode
}) {
  return (
    <Box>
      <Heading size="4" mb="1">
        {title}
      </Heading>
      <Text as="p" color="gray" size="2" mb="3">
        {subtitle}
      </Text>
      {children}
    </Box>
  )
}

export default function DashboardIndex({
  weight_history,
  recent_days,
  week_stats,
}: DashboardIndexProps) {
  const confirmedDays = recent_days.filter((day) => !day.is_today && day.is_confirmed)

  return (
    <>
      <Head title="Dashboard" />

      <Flex direction="column" gap="5">
        <DashboardSection
          title="Weight history"
          subtitle="Every measurement and the weekly average across eight weeks, including this week."
        >
          <WeightHistoryChart history={weight_history} />
        </DashboardSection>

        <DashboardSection
          title="Today and last seven completed days"
          subtitle={`The iPhone calorie badge averages confirmed totals from yesterday through seven days ago. ${confirmedDays.length}/7 days included.`}
        >
          <Table.Root variant="surface">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Day</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell justify="end">Calories</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell justify="end">Weight</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell justify="end">Average weight</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {recent_days.map((day) => (
                <Table.Row key={day.date_string}>
                  <Table.RowHeaderCell>{formatDayDate(day.date_string)}</Table.RowHeaderCell>
                  <Table.Cell justify="end">
                    {!day.is_today && (
                      <Flex align="center" gap="2" justify="end">
                        <Text>{formatCalorieTotal(day.total_calories)}</Text>
                        <TrackingIcon isConfirmed={day.is_confirmed} />
                      </Flex>
                    )}
                  </Table.Cell>
                  <Table.Cell justify="end">{formatWeight(day.weight_kg)}</Table.Cell>
                  <Table.Cell justify="end">{formatWeight(day.weight_trend_kg)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </DashboardSection>

        <DashboardSection
          title="Recent weeks"
          subtitle="Weekly calorie averages, weight pace, and how many completed days were fully tracked."
        >
          {week_stats.length > 0 ? (
            <Table.Root variant="surface">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Week</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell justify="end">
                    Average calories
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell justify="end">
                    Weight change
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell justify="end">
                    Tracked days
                  </Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {week_stats.map((weekStat) => (
                  <Table.Row key={weekStat.id}>
                    <Table.RowHeaderCell>
                      {formatWeekRange(weekStat.week_start_date)}
                    </Table.RowHeaderCell>
                    <Table.Cell justify="end">
                      {formatCalorieAverage(weekStat.average_calories)}
                    </Table.Cell>
                    <Table.Cell justify="end">
                      {formatWeightChange(weekStat.weight_change_trend_kg)}
                    </Table.Cell>
                    <Table.Cell justify="end">
                      {formatTrackedDays(weekStat)}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          ) : (
            <Box p="5">
              <Text color="gray" size="2">
                No weekly stats yet.
              </Text>
            </Box>
          )}
        </DashboardSection>
      </Flex>
    </>
  )
}
